import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Star, Wifi, Car, Wind, MapPin, Phone, Mail, Trees, Home as HomeIcon, MessageCircle, Facebook } from 'lucide-react';
import { studiosData } from '../data/studiosData';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // For now, just show alert
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 backdrop-blur-lg bg-white/80">
        <div className="container mx-auto px-6 py-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <HomeIcon className="w-5 h-5 text-gray-900" />
            <h1 className="text-xl font-medium text-gray-900">{studiosData.name}</h1>
          </div>
          <nav className="hidden md:flex gap-8 items-center">
            <a href="#about" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">About</a>
            <a href="#amenities" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Amenities</a>
            <a href="#reviews" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Reviews</a>
            <a href="#gallery" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Gallery</a>
            <a href="#contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
            <a 
              href={studiosData.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-medium transition-all"
            >
              Book Now
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={studiosData.images[0]} 
            alt="Damaskis Studios" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/10"></div>
        </div>
        <div className="relative container mx-auto px-6 h-full flex items-center">
          <div className="max-w-2xl">
            <h2 className="text-6xl md:text-7xl font-light mb-6 text-white tracking-tight">{studiosData.name}</h2>
            <p className="text-xl md:text-2xl mb-8 text-white/90 font-light">{studiosData.tagline}</p>
            <div className="flex items-center gap-6 mb-10">
              <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-6 py-3 rounded-full">
                <Star className="w-4 h-4 fill-gray-900 text-gray-900" />
                <span className="font-semibold text-gray-900">{studiosData.rating}</span>
                <span className="text-sm text-gray-600">({studiosData.reviewCount})</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">{studiosData.location}</span>
              </div>
            </div>
            <div className="flex gap-4">
              <a 
                href={studiosData.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-10 py-6 text-base rounded-full font-medium">
                  Book Now
                </Button>
              </a>
              <a href="#contact">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-white/30 px-8 py-6 text-base rounded-full">
                  Contact Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 container mx-auto px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">Welcome to Damaskis Studios</h3>
          <p className="text-lg text-gray-600 leading-relaxed font-light max-w-3xl mx-auto">
            {studiosData.description}
          </p>
          <div className="grid md:grid-cols-3 gap-16 mt-20">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-gray-900" />
              </div>
              <h4 className="font-medium text-lg mb-2 text-gray-900">Prime Location</h4>
              <p className="text-sm text-gray-500">Rated {studiosData.locationRating}/10</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <Star className="w-7 h-7 text-gray-900" />
              </div>
              <h4 className="font-medium text-lg mb-2 text-gray-900">Exceptional</h4>
              <p className="text-sm text-gray-500">9.2/10 rating</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <Trees className="w-7 h-7 text-gray-900" />
              </div>
              <h4 className="font-medium text-lg mb-2 text-gray-900">Garden & Terrace</h4>
              <p className="text-sm text-gray-500">Private outdoor space</p>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">Amenities</h3>
            <p className="text-gray-500 text-sm">Everything you need for a comfortable stay</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {studiosData.amenities.map((amenity, index) => {
              const IconComponent = amenity.icon;
              return (
                <Card key={index} className="p-8 bg-white border-gray-100 hover:shadow-lg transition-all duration-300 rounded-2xl">
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-6 h-6 text-gray-900" />
                    </div>
                    <h4 className="font-medium text-base mb-2 text-gray-900">{amenity.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{amenity.description}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">Guest Reviews</h3>
            <p className="text-gray-500 text-sm">What our guests have to say</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {studiosData.reviews.map((review, index) => (
              <Card key={index} className="p-8 bg-gray-50 border-0 hover:shadow-lg transition-all duration-300 rounded-2xl">
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gray-900 text-gray-900" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  "{review.comment}"
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-medium text-sm">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{review.name}</p>
                    <p className="text-xs text-gray-500">{review.country}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">Gallery</h3>
            <p className="text-gray-500 text-sm">Take a visual tour of our studios</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {studiosData.images.slice(1).map((image, index) => (
              <div key={index} className="relative overflow-hidden group aspect-[4/3] rounded-2xl">
                <img 
                  src={image} 
                  alt={`Damaskis Studios ${index + 1}`} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">Get in Touch</h3>
            <p className="text-gray-500 text-sm">We'd love to hear from you</p>
          </div>
          <div className="grid md:grid-cols-2 gap-16 max-w-6xl mx-auto">
            <div>
              <h4 className="text-2xl font-light mb-10 text-gray-900">Contact Information</h4>
              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-gray-900" />
                  </div>
                  <div>
                    <h5 className="font-medium text-base mb-2 text-gray-900">Location</h5>
                    <p className="text-gray-600 text-sm leading-relaxed">{studiosData.address}</p>
                    <p className="text-gray-600 text-sm">{studiosData.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-gray-900" />
                  </div>
                  <div>
                    <h5 className="font-medium text-base mb-2 text-gray-900">Email</h5>
                    <a href={`mailto:${studiosData.email}`} className="text-gray-600 text-sm hover:text-gray-900 transition-colors">
                      {studiosData.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-gray-900" />
                  </div>
                  <div>
                    <h5 className="font-medium text-base mb-2 text-gray-900">Phone</h5>
                    <a href={`tel:${studiosData.phone}`} className="text-gray-600 text-sm hover:text-gray-900 transition-colors">
                      {studiosData.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-base mb-2 text-gray-900">WhatsApp</h5>
                    <a 
                      href={`https://wa.me/${studiosData.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 text-sm hover:text-green-600 transition-colors"
                    >
                      {studiosData.whatsapp}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-base mb-2 text-gray-900">Facebook</h5>
                    <a 
                      href={studiosData.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 text-sm hover:text-blue-600 transition-colors"
                    >
                      Damaskis Studios
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-10 h-72 bg-gray-100 rounded-2xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3069.8753926357583!2d19.9236!3d39.7869!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135b1e5e5e5e5e5e%3A0x5e5e5e5e5e5e5e5e!2sKassiopi%2C%20Greece!5e0!3m2!1sen!2s!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Damaskis Studios Location"
                ></iframe>
              </div>
            </div>
            <div>
              <Card className="p-10 bg-gray-50 border-0 rounded-2xl">
                <h4 className="text-2xl font-light mb-8 text-gray-900">Send a Message</h4>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-3">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="border-gray-200 focus:border-gray-900 bg-white rounded-lg py-6"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-3">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="border-gray-200 focus:border-gray-900 bg-white rounded-lg py-6"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-3">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your message..."
                      rows={6}
                      required
                      className="border-gray-200 focus:border-gray-900 bg-white rounded-lg"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 rounded-full text-sm font-medium">
                    Send Message
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 text-gray-600 py-16 border-t border-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h5 className="text-xl font-medium mb-4 text-gray-900">{studiosData.name}</h5>
              <p className="text-gray-500 leading-relaxed">{studiosData.tagline}</p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-6 text-gray-900">Quick Links</h5>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-500 hover:text-gray-900 transition-colors text-sm">About</a></li>
                <li><a href="#amenities" className="text-gray-500 hover:text-gray-900 transition-colors text-sm">Amenities</a></li>
                <li><a href="#reviews" className="text-gray-500 hover:text-gray-900 transition-colors text-sm">Reviews</a></li>
                <li><a href="#contact" className="text-gray-500 hover:text-gray-900 transition-colors text-sm">Contact</a></li>
                <li>
                  <a 
                    href={studiosData.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-900 hover:text-gray-700 transition-colors text-sm font-medium"
                  >
                    Book Online
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-6 text-gray-900">Connect With Us</h5>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <a 
                    href={studiosData.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors text-sm"
                  >
                    <Facebook className="w-4 h-4" />
                    <span>Facebook</span>
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href={`https://wa.me/${studiosData.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-500 hover:text-green-600 transition-colors text-sm"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href={`mailto:${studiosData.email}`}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} {studiosData.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;